import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { firebaseApp } from '../utils/firebaseConfig';

const db = getFirestore(firebaseApp);

export interface CouponResult {
  valid: true;
  code: string;
  discountType: 'free' | 'percent' | 'discount';
  discountValue: number;
  storePlanNamePostfix?: string;
}

interface Props {
  onValidCoupon: (result: CouponResult) => void;
  onClearCoupon: () => void;
  /** Compact centered layout used by the V2 payments page */
  compact?: boolean;
}

const CouponInput: React.FC<Props> = ({ onValidCoupon, onClearCoupon, compact = false }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState<CouponResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(collection(db, 'coupons'), where('code', '==', trimmed)));
      if (snap.empty) {
        setError(t('coupon_invalid', 'Invalid coupon code'));
        return;
      }
      const data = snap.docs[0].data() as {
        discountPercentage?: number;
        discountType?: 'free' | 'percent' | 'discount';
        discountValue?: number;
        isActive?: boolean;
        storePlanNamePostfix?: string;
      };
      if (data.isActive === false) {
        setError(t('coupon_inactive', 'This coupon is no longer active'));
        return;
      }
      const discountType = data.discountType;
      const discountValue =
        discountType === 'discount'
          ? data.discountPercentage
          : (data.discountValue ?? data.discountPercentage);
      if (!discountType || discountValue == null) {
        setError(t('coupon_invalid', 'Invalid coupon code'));
        return;
      }
      const result: CouponResult = {
        valid: true,
        code: trimmed,
        discountType,
        discountValue,
        storePlanNamePostfix: data.storePlanNamePostfix,
      };
      setApplied(result);
      onValidCoupon(result);
    } catch {
      setError(t('coupon_error', 'Failed to validate coupon. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setApplied(null);
    setCode('');
    setError(null);
    onClearCoupon();
  };

  const discountLabel = (result: CouponResult) =>
    result.discountType === 'free'
      ? t('coupon_free_days', '{{days}} free days', { days: result.discountValue })
      : t('coupon_percent', '{{pct}}% off', { pct: result.discountValue });

  if (compact) {
    if (applied) {
      return (
        <div className="mx-auto flex max-w-[300px] items-center justify-between gap-2 rounded-xl border border-green-300 bg-green-100 px-3 py-1.5">
          <span className="text-sm text-green-800">
            <strong>{applied.code}</strong>
            {' — '}
            {discountLabel(applied)}
          </span>
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('coupon_remove', 'Remove coupon') as string}
            className="border-none bg-transparent p-0 text-lg leading-none text-green-800 cursor-pointer"
          >
            ×
          </button>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-[300px]">
        <div className="flex items-center gap-1.5 rounded-xl border border-gray-500 bg-white p-1.5">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && void handleApply()}
            placeholder={t('payments_coupon_placeholder', 'Discount code? Enter here') as string}
            className="min-w-0 flex-1 border-none bg-transparent px-2 text-[15px] font-semibold text-gray-800 outline-none placeholder:text-gray-500"
          />
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={loading || !code.trim()}
            className={`rounded-lg border-none bg-[#6B7280] px-3 py-1 text-sm font-bold text-white ${
              loading || !code.trim() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            {loading ? '…' : t('coupon_apply', 'Apply')}
          </button>
        </div>
        {error && <p className="m-0 mt-1.5 text-center text-[13px] text-red-800">{error}</p>}
      </div>
    );
  }

  if (applied) {
    return (
      <div
        style={{
          marginTop: 16,
          padding: '10px 14px',
          borderRadius: 8,
          background: '#D1FAE5',
          border: '1px solid #6EE7B7',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#065F46', fontSize: 14 }}>
          <strong>{applied.code}</strong>
          {' — '}
          {discountLabel(applied)}
        </span>
        <button
          type="button"
          onClick={handleClear}
          aria-label={t('coupon_remove', 'Remove coupon') as string}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#065F46',
            fontSize: 18,
            lineHeight: 1,
            padding: '0 0 0 8px',
          }}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && void handleApply()}
          placeholder={t('coupon_placeholder', 'Coupon code') as string}
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #D1D5DB',
            fontSize: 15,
            fontFamily: 'monospace',
            letterSpacing: '0.05em',
          }}
        />
        <button
          type="button"
          onClick={() => void handleApply()}
          disabled={loading || !code.trim()}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            background: '#4F46E5',
            color: '#fff',
            border: 'none',
            cursor: loading || !code.trim() ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 15,
            opacity: loading || !code.trim() ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? '…' : t('coupon_apply', 'Apply')}
        </button>
      </div>
      {error && <p style={{ margin: '6px 0 0 0', color: '#991B1B', fontSize: 13 }}>{error}</p>}
    </div>
  );
};

export default CouponInput;
